import { useCallback, useState } from 'react';

import { useParams } from 'react-router-dom';

import { AddChronologyFactDialog } from './dialogs/add-chronology-fact-dialog';
import { ViewSelector } from './view-selector';
import {
  DownloadFormat,
  StyleFormat,
  DocumentDownloadOptions,
} from '../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useChronologyDownload from '@/api/mutations/useDownloadChronology';
import { DownloadDialog } from '@/new/components/dialogs/download-dialog';
import { ChronologyExportProgressModal } from '@/new/components/dialogs/export-progress-dialog/chronology-export-progress';
import { Icon } from '@/new/components/icon';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';

enum ViewMode {
  Table = 'table',
  Visual = 'visual',
}

interface ChronologyTableToolbarProps {
  chronologyTitle: string;
  goToPage: (value: number) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  canGetPrevPage: boolean;
  canGetNextPage: boolean;
  prevPage: () => void;
  nextPage: () => void;
  currentPage: number;
  noOfPages: number | undefined;
}

export function ChronologyTimelineHeader({
  chronologyTitle,
  canGetPrevPage,
  canGetNextPage,
  prevPage,
  nextPage,
  currentPage,
  noOfPages,
  goToPage,
  viewMode,
  onViewModeChange,
}: ChronologyTableToolbarProps) {
  const { chronologyId, matterId, subsetId } = useParams();
  if (!chronologyId || !matterId || !subsetId) return null;

  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);
  const [addChronologyFactDialogIsOpen, setAddChronologyFactDialogIsOpen] = useState(false);
  const [exportProgressModalIsOpen, setExportProgressModalIsOpen] = useState(false);

  const [isExportCreating, setIsExportCreating] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const [currentExportId, setCurrentExportId] = useState<string | null>(null);

  const { downloadMutation, downloadWithDocuments } = useChronologyDownload({
    chronologyId,
    matterId,
    subsetId,
  });
  const { mutateAsync: fetchDownload, isLoading: isLoadingDownload } = downloadMutation;

  // Handlers
  const handleDownloadCSV = useCallback(
    async ({
      title,
      columns,
      downloadFormat,
      includeSourceDocuments,
    }: {
      title: string;
      columns: string[];
      downloadFormat: DownloadFormat;
      includeSourceDocuments?: boolean;
    }) => {
      if (downloadFormat === DownloadFormat.EXCEL) {
        trackCustomEvent(TrackingEventsV2.DownloadChronologyTableCSV, { chronologyId, subsetId });
      } else if (downloadFormat === DownloadFormat.DOCX) {
        trackCustomEvent(TrackingEventsV2.DownloadChronologyTableDocx, { chronologyId, subsetId });
      }

      if (includeSourceDocuments) {
        trackCustomEvent(TrackingEventsV2.DownloadChronologyWithDocuments, { chronologyId, subsetId });

        try {
          const result = await fetchDownload({
            title,
            columns,
            styleFormat: StyleFormat.Table,
            downloadFormat,
            includeSourceDocuments,
          });

          if (result) {
            setDownloadModalIsOpen(false);
            setCurrentExportId(result.exportId);
            setExportProgressModalIsOpen(true);
            setIsExportCreating(true);
            setExportError(undefined);
          }
        } catch (error) {
          console.error('Error starting chronology export:', error);
          setDownloadModalIsOpen(false);
          setExportError(error instanceof Error ? error.message : 'Export failed');
          setExportProgressModalIsOpen(true);
          setIsExportCreating(false);
        }
      } else {
        await fetchDownload({
          title,
          columns,
          styleFormat: StyleFormat.Table,
          downloadFormat,
          includeSourceDocuments: false,
        });
        setDownloadModalIsOpen(false);
      }
    },
    [fetchDownload, chronologyId, subsetId],
  );

  const handleDownloadWord = useCallback(
    async ({
      title,
      style,
      downloadFormat,
      documentOptions,
      includeSourceDocuments,
    }: {
      title: string;
      downloadFormat: DownloadFormat;
      style?: string;
      documentOptions?: DocumentDownloadOptions;
      includeSourceDocuments?: boolean;
    }) => {
      trackCustomEvent(TrackingEventsV2.DownloadChronologyWrittenDocx, { chronologyId, subsetId });
      if (style !== '') {
        trackCustomEvent(TrackingEventsV2.DownloadChronologyWrittenCustomStyle, { chronologyId, subsetId });
      }

      if (includeSourceDocuments) {
        trackCustomEvent(TrackingEventsV2.DownloadChronologyWithDocuments, { chronologyId, subsetId });

        try {
          const result = await fetchDownload({
            title,
            style,
            styleFormat: StyleFormat.Written,
            downloadFormat,
            documentOptions,
            includeSourceDocuments,
          });

          if (result) {
            setDownloadModalIsOpen(false);
            setCurrentExportId(result.exportId);
            setExportProgressModalIsOpen(true);
            setIsExportCreating(true);
            setExportError(undefined);
          }
        } catch (error) {
          console.error('Error starting chronology export:', error);
          setDownloadModalIsOpen(false);
          setExportError(error instanceof Error ? error.message : 'Export failed');
          setExportProgressModalIsOpen(true);
          setIsExportCreating(false);
        }
      } else {
        await fetchDownload({
          title,
          style,
          styleFormat: StyleFormat.Written,
          downloadFormat,
          documentOptions,
          includeSourceDocuments: false,
        });
        setDownloadModalIsOpen(false);
      }
    },
    [fetchDownload, chronologyId, subsetId],
  );

  return (
    <div className="flex gap-2">
      {viewMode !== ViewMode.Visual ? (
        <div className="flex items-center gap-2">
          <Button onClick={() => setAddChronologyFactDialogIsOpen(true)}>
            <Icon className="lucide--plus-circle" /> Add Fact
          </Button>

          <Button onClick={() => setDownloadModalIsOpen(true)}>
            <Icon className="lucide--download" />
            Export Chronology
          </Button>
        </div>
      ) : (
        <div></div>
      )}

      <div className="flex items-center gap-2">
        <Pagination
          canGetPrevPage={canGetPrevPage}
          canGetNextPage={canGetNextPage}
          prevPage={prevPage}
          nextPage={nextPage}
          currentPage={currentPage}
          noOfPages={noOfPages}
          goToPage={goToPage}
        />

        <ViewSelector viewMode={viewMode} onViewModeChange={onViewModeChange} />
      </div>

      <AddChronologyFactDialog isOpen={addChronologyFactDialogIsOpen} onOpenChange={setAddChronologyFactDialogIsOpen} />

      <DownloadDialog
        isOpen={downloadModalIsOpen}
        onClose={() => setDownloadModalIsOpen(false)}
        modalTitle="Export Chronology"
        downloadTitle={chronologyTitle}
        isLoading={isLoadingDownload}
        columnOptions={chronologyDownloadColumnOptions}
        onDownloadTable={handleDownloadCSV}
        onDownloadWritten={handleDownloadWord}
        displayOptions={{
          showStyle: true,
          showDocumentOptions: false,
          showSourceDocumentsOption: true,
        }}
        showEmailNotification
      />

      <ChronologyExportProgressModal
        isOpen={exportProgressModalIsOpen}
        onClose={() => {
          setExportProgressModalIsOpen(false);
          setIsExportCreating(false);
          setExportError(undefined);
          setCurrentExportId(null);
        }}
        matterId={matterId}
        subsetId={subsetId}
        chronologyId={chronologyId}
        chronologyTitle={chronologyTitle}
        onDownload={async () => {
          if (currentExportId) {
            await downloadWithDocuments(chronologyTitle, currentExportId);
          }
        }}
        isCreating={isExportCreating}
        error={exportError}
      />
    </div>
  );
}

const chronologyDownloadColumnOptions = [
  'Date (verbatim)',
  'Date (standardised)',
  'Fact',
  'Relevance',
  'Source Text',
  'Source Document',
  'Significance',
  'Id',
  'Notes',
  'Notes Date',
  'Notes By',
  'Verified By',
  'Verified Date',
];
